import { useState } from "react";
import { Modal, TextInput, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import Lottie from "lottie-react";
import { PostHog } from "posthog-js";
import { emailSchema } from "../../../components/shared/claimable-authenticator-v2/validation-schemas";
import { Button } from "../../../components/product/buttons/button";
import checkmarkAnimation from "../../../animations/checkmark.json";
import validationStyles from "../../../styles/product/validation.module.scss";
import styles from "./cant-find-modal.module.scss";

interface CantFindModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchText: string;
  posthog: PostHog;
}

interface CantFindFormValues {
  searchText: string;
  email: string;
}

const MAX_DESCRIPTION_LENGTH = 150;

export default function CantFindModal({
  isOpen,
  onClose,
  searchText,
  posthog,
}: CantFindModalProps) {
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<CantFindFormValues>({
    initialValues: {
      searchText: searchText || "",
      email: "",
    },
    validate: {
      searchText: (value) => {
        if (value.length > MAX_DESCRIPTION_LENGTH)
          return `Must be ${MAX_DESCRIPTION_LENGTH} characters or less`;
        return null;
      },
      email: (value) => {
        if (!value.trim()) return "Email is required";
        const result = emailSchema.safeParse(value);
        if (!result.success) return "Enter a valid email";
        return null;
      },
    },
    validateInputOnBlur: true,
    validateInputOnChange: true,
  });

  const handleSubmit = (values: CantFindFormValues) => {
    posthog.capture("eligibility_cant_find_submitted", {
      originalSearchText: searchText,
      deniedCoverageDescription: values.searchText,
      email: values.email,
      descriptionWasEdited: values.searchText !== searchText,
    });

    setIsSuccess(true);

    setTimeout(() => {
      setIsSuccess(false);
      form.reset();
      onClose();
    }, 5000);
  };

  const handleClose = () => {
    setIsSuccess(false);
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={isSuccess ? undefined : "I can't find my denied care"}
      size="lg"
      data-testid="cant-find-modal"
    >
      {isSuccess ? (
        <div className={styles.successBody}>
          <Lottie
            animationData={checkmarkAnimation}
            loop={false}
            className={styles.successAnimation}
          />
          <p className={styles.successTitle}>{"You're on the list"}</p>
          <p className={styles.successDescription}>
            {
              "Got it. We'll reach out when we add support for what you're looking for."
            }
          </p>
        </div>
      ) : (
        <div className={styles.modalBody}>
          <p className={styles.description}>
            Every Claimable appeal is fully customized to make sure our appeal
            is accurate and built to win. That means that we don&apos;t support
            every medication and condition &ndash; yet. We add support for new
            medications every month. Fill out your information below and
            we&apos;ll let you know when we can help. In the mean time,{" "}
            <a
              href="https://www.getclaimable.com/post/cant-find-medication-on-claimable"
              target="_blank"
              rel="noopener noreferrer"
            >
              click here
            </a>{" "}
            for more appeal resources and support.
          </p>
          <form onSubmit={form.onSubmit(handleSubmit)} className={styles.form}>
            <div>
              <Textarea
                label="What were you denied coverage for?"
                placeholder={
                  form.values.searchText || "e.g., Keytruda for Lung Cancer"
                }
                data-testid="cant-find-search-text"
                classNames={{ error: validationStyles.formError }}
                {...form.getInputProps("searchText")}
              />
              <p className={styles.charCount}>
                {form.values.searchText.length}/{MAX_DESCRIPTION_LENGTH}
              </p>
            </div>
            <TextInput
              label="Email address"
              placeholder="you@email.com"
              type="email"
              maxLength={254}
              data-testid="cant-find-email"
              classNames={{ error: validationStyles.formError }}
              {...form.getInputProps("email")}
            />
            <div className={styles.submitButton}>
              <Button
                variant="primary"
                size="medium"
                type="submit"
                data-testid="cant-find-submit"
              >
                Notify Me
              </Button>
            </div>
          </form>
        </div>
      )}
    </Modal>
  );
}
